import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import sitemapRoutes from "@/sitemap-routes";

const SITE_NAME = "Rinqel";
const BASE_URL = "https://rinqel.nl";

// Build labelMap dynamically from the central config
const labelMap: Record<string, string> = {};
sitemapRoutes.forEach((r) => {
  if (r.label) {
    const slug = r.path.split("/").pop()!;
    labelMap[slug] = r.label;
  }
});

interface SEOProps {
  title?: string;
  description?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

const SEO = ({
  title,
  description,
  type = "website",
  jsonLd,
  noIndex = false,
}: SEOProps) => {
  const { pathname } = useLocation();
  const canonicalUrl = `${BASE_URL}${pathname}`;

  // Match current path against the central route config to look up dates.
  const currentRoute = sitemapRoutes.find((r) => r.path === pathname);
  const datePublished = currentRoute?.publishedAt;
  const dateModified = currentRoute?.lastmod ?? currentRoute?.publishedAt;

  // Titles worden 1-op-1 uit `seoTitle` overgenomen — pagina's leveren de
  // volledige title aan (incl. eventueel "| Rinqel"-suffix). Geen auto-append
  // meer, zodat we per route exact onder de 60-tekens-grens kunnen blijven.
  const fullTitle = title ?? `${SITE_NAME} — IT zonder gedoe`;

  // Collect JSON-LD schemas (Organization on every page so Service/Article references resolve,
  // WebSite on homepage, custom, BreadcrumbList)
  const schemas: Record<string, unknown>[] = [];

  // Always inject the LocalBusiness/Organization entity with the same @id so every page can reference it.
  schemas.push({
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": "https://rinqel.nl/#organization",
    name: "Rinqel",
    legalName: "Rinqel",
    slogan: "Altijd inclusief — IT zonder gedoe",
    url: "https://rinqel.nl",
    logo: "https://rinqel.nl/logo-rinqel.png",
    image: "https://rinqel.nl/og-image.png",
    description:
      "Rinqel is jouw IT-partner voor internet, telefonie, werkplekken en meer. Vast laag maandbedrag, geen verrassingen.",
    telephone: "+31858223400",
    email: "contact@rinqel.nl",
    priceRange: "€€",
    currenciesAccepted: "EUR",
    paymentAccepted: "Automatische incasso, Factuur",
    // KvK-nummer als PropertyValue identifier — verankert de entiteit officieel.
    identifier: {
      "@type": "PropertyValue",
      propertyID: "KvK",
      value: "64926710",
    },
    brand: { "@type": "Brand", name: "Rinqel" },
    knowsAbout: [
      "Microsoft 365",
      "Microsoft Intune",
      "Microsoft Defender XDR",
      "Zakelijke VoIP-telefonie",
      "Zakelijk mobiel",
      "Zakelijk internet",
      "Managed werkplek",
      "Passkeys",
      "Bring Your Own Device",
      "SharePoint",
      "OneDrive",
      "Exchange Online",
    ],
    makesOffer: { "@id": "https://rinqel.nl/#catalog" },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Rijksweg 42a",
      postalCode: "1906 BJ",
      addressLocality: "Limmen",
      addressRegion: "Noord-Holland",
      addressCountry: "NL",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 52.5694,
      longitude: 4.6989,
    },
    hasMap: "https://maps.app.goo.gl/fjCgnkUTePEbzge27",
    areaServed: { "@type": "Country", name: "Nederland" },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+31858223400",
        email: "contact@rinqel.nl",
        contactType: "customer service",
        areaServed: "NL",
        availableLanguage: ["Dutch", "English"],
      },
    ],
    // Officiële externe profielen en de Google Maps/Business-vermelding.
    // Alleen URLs die daadwerkelijk naar Rinqel verwijzen; rinqel.it is een
    // 301-forward naar rinqel.nl en hoort daarom niet in sameAs.
    sameAs: [
      "https://www.linkedin.com/company/rinqel",
      "https://www.facebook.com/Rinqel/",
      "https://maps.app.goo.gl/fjCgnkUTePEbzge27",
      "https://www.google.com/search?kgmid=/g/11f5h2543j",
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "17:00",
      },
    ],
  });

  if (pathname === "/") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${BASE_URL}/#website`,
      name: SITE_NAME,
      url: BASE_URL,
      // Google kan op basis hiervan een zoekbalk direct in de zoekresultaten tonen.
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${BASE_URL}/zoeken?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    });
  }

  // Per-pagina WebPage entiteit met eerlijke publicatie- en wijzigingsdatum.
  const speakablePaths = new Set(["/", "/diensten", "/faq", "/contact"]);
  if (datePublished) {
    const webPage: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": pathname === "/contact" ? "ContactPage" : "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: fullTitle,
      ...(description ? { description } : {}),
      inLanguage: "nl-NL",
      isPartOf: { "@type": "WebSite", "@id": `${BASE_URL}/#website`, url: BASE_URL, name: SITE_NAME },
      publisher: { "@id": "https://rinqel.nl/#organization" },
      datePublished,
      ...(dateModified ? { dateModified } : {}),
    };
    if (pathname === "/contact") {
      webPage.mainEntity = { "@id": "https://rinqel.nl/#organization" };
    }
    if (speakablePaths.has(pathname)) {
      webPage.speakable = {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "[data-speakable]"],
      };
    }
    schemas.push(webPage);
  }

  if (jsonLd) {
    if (Array.isArray(jsonLd)) schemas.push(...jsonLd);
    else schemas.push(jsonLd);
  }

  if (pathname !== "/") {
    const segments = pathname.split("/").filter(Boolean);
    const breadcrumbItems: Record<string, unknown>[] = [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
    ];

    let path = "";
    segments.forEach((segment, i) => {
      path += `/${segment}`;
      breadcrumbItems.push({
        "@type": "ListItem",
        position: i + 2,
        name: labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1),
        item: `${BASE_URL}${path}`,
      });
    });

    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems,
    });
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonicalUrl} />
      {/* Geen hreflang: rinqel.it is een 301-forward, dus er bestaat één
          publieke URL. Self-referencing hreflang voegt niets toe en suggereert
          ten onrechte een tweede taal-/regio-variant. */}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:image" content={`${BASE_URL}/og-image.png`} />

      {/* Twitter / X Card — summary_large_image gebruikt dezelfde og:image (1216x640) */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={`${BASE_URL}/og-image.png`} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — IT zonder gedoe`} />
      <meta name="twitter:url" content={canonicalUrl} />

      {/* JSON-LD */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
